// Live Call — the statistics console, on the floating shell.
//
// Same screen as before: a titled header, Live / History tabs, the day's five
// counters, the floor strip with the availability toggle, the live queue and
// the last-updated stamp. What changed is the surface — cards sit ON the
// canvas with the product's tokens, radii and shadow, so this screen reads as
// part of the same application as Inbox, Tickets and Customers instead of a
// white panel borrowed from somewhere else.
//
// Nothing the console could do was dropped. The call itself still gets a band
// at the top of the Live tab while one is connected — number, clock, recording
// state — with mute, hang up and the way through to the full context screen in
// the header. Everything the handoff carried (transcript, the assistant's
// summary, the ticket link) lives on /live-call/active, which that band links
// to.

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  Phone,
  PhoneCall,
  Bot,
  User,
  Users,
  Clock,
  ListOrdered,
  Mic,
  MicOff,
  RefreshCw,
  Trash2,
  Wifi,
  WifiOff,
  ChevronDown,
  Grid3x3,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { voiceService, type LiveCallStats, type VoiceConfig } from '@/services/voice.service';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useVoiceAgentStore } from '@stores/voiceAgentStore';
import { useAuthStore } from '@stores/authStore';
import { VoiceCallHistory } from '@components/voice/VoiceCallHistory';
import { VoiceQueue } from '@components/voice/VoiceQueue';
import { VoiceSessionDetail } from '@components/voice/VoiceSessionDetail';
import { AgentRoster } from './components/AgentRoster';
import { DialPad } from './components/DialPad';
import { callControls } from './callConference';
import { outgoingCallLabel } from './outboundCall';
import { useOutboundAvailable } from './useOutboundAvailable';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';
import { ConfirmDialog } from '@centraoneplus/ui';
import { callClock } from './callConsole';

/** Ask for the microphone; the stream is released at once, only the permission is wanted. */
export async function requestMicrophone(): Promise<'granted' | 'denied' | 'unknown'> {
  const media = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
  if (!media?.getUserMedia) return 'unknown';
  try {
    const stream = await media.getUserMedia({ audio: true });
    stream.getTracks().forEach((t) => t.stop());
    return 'granted';
  } catch (err: any) {
    return err?.name === 'NotAllowedError' || err?.name === 'SecurityError' ? 'denied' : 'unknown';
  }
}

export type AgentReadiness = { tone: 'ok' | 'busy' | 'warn' | 'off'; label: string; hint: string };

/** What the agent can do right now, and if they cannot take calls, why. */
export function agentReadiness(s: {
  isOnline: boolean;
  onCall: boolean;
  isDeviceReady: boolean;
  isLive: boolean;
  phoneError: string | null;
  micState: 'unknown' | 'granted' | 'denied';
}): AgentReadiness {
  if (!s.isOnline) return { tone: 'off', label: 'Offline', hint: 'Go online to be offered calls.' };
  if (s.onCall) return { tone: 'busy', label: 'On a call', hint: 'New calls go to other agents until you finish.' };
  if (s.micState === 'denied') {
    return { tone: 'warn', label: "Can't take calls", hint: 'Your microphone is blocked. Allow it in the browser address bar.' };
  }
  if (s.phoneError) return { tone: 'warn', label: "Can't take calls", hint: s.phoneError };
  if (!s.isLive) {
    return { tone: 'warn', label: "Can't take calls", hint: 'Live updates are disconnected. Reconnecting…' };
  }
  if (!s.isDeviceReady) return { tone: 'warn', label: 'Getting ready', hint: 'Setting up your phone…' };
  return { tone: 'ok', label: 'Available', hint: 'Callers who ask for a person will ring here.' };
}

const READINESS_TONE: Record<AgentReadiness['tone'], { wash: string; line: string; ink: string; dot: string }> = {
  ok: { wash: 'var(--co-green-wash)', line: 'var(--co-green-line)', ink: 'var(--co-green-ink)', dot: 'var(--co-green)' },
  busy: { wash: 'var(--co-burgundy-wash)', line: 'var(--co-burgundy-line)', ink: 'var(--co-burgundy)', dot: 'var(--co-burgundy)' },
  warn: { wash: 'var(--co-terracotta-wash)', line: 'var(--co-terracotta-line)', ink: 'var(--co-terracotta-ink)', dot: 'var(--co-terracotta)' },
  off: { wash: 'var(--co-sunken)', line: 'var(--co-line)', ink: 'var(--co-ink-3)', dot: 'var(--co-line-strong)' },
};

/** The floating shell: cards sit ON the canvas, they do not butt against it. */
const CARD: React.CSSProperties = { borderRadius: 10, boxShadow: 'var(--co-shadow-card)' };
const CARD_CLASS = 'border border-[var(--co-line)] bg-[var(--co-raised)]';

const CONTROL =
  'flex h-[34px] flex-none items-center gap-[7px] whitespace-nowrap rounded-lg border ' +
  'border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[12.5px] ' +
  'text-[var(--co-ink-2)] hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)] ' +
  'disabled:cursor-not-allowed disabled:opacity-50';
const PRIMARY =
  'flex h-[34px] flex-none items-center gap-[7px] rounded-lg bg-[var(--co-brass-ink)] px-[14px] ' +
  'text-[12.5px] font-bold text-[var(--co-brass-on)] hover:opacity-90 ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

/** Header-only variants: the header's controls take the Board's 10px radius. */
const HEADER_CONTROL = CONTROL.replace('rounded-lg', 'rounded-[10px]');
const HEADER_PRIMARY = PRIMARY.replace('rounded-lg', 'rounded-[10px]');

/** The five hues of the counters, spoken in tokens rather than raw colour. */
type Accent = 'brass' | 'green' | 'machine' | 'burgundy' | 'terracotta';

const ACCENT: Record<Accent, { wash: string; line: string; ink: string }> = {
  brass: { wash: 'var(--co-brass-wash)', line: 'var(--co-brass-line)', ink: 'var(--co-brass-ink)' },
  green: { wash: 'var(--co-green-wash)', line: 'var(--co-green-line)', ink: 'var(--co-green-ink)' },
  machine: {
    wash: 'var(--co-machine-wash)',
    line: 'var(--co-machine-line)',
    ink: 'var(--co-machine-ink)',
  },
  burgundy: {
    wash: 'var(--co-burgundy-wash)',
    line: 'var(--co-burgundy-line)',
    ink: 'var(--co-burgundy)',
  },
  terracotta: {
    wash: 'var(--co-terracotta-wash)',
    line: 'var(--co-terracotta-line)',
    ink: 'var(--co-terracotta-ink)',
  },
};

type TabType = 'live' | 'history';

export default function LiveCallPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const {
    activeCall,
    callContext,
    isMuted,
    isOnline,
    isDeviceReady,
    callDurationSeconds,
    mute,
    unmute,
    hangup,
    setOnline,
    setCallDuration,
  } = useVoiceAgentStore();

  const [activeTab, setActiveTab] = useState<TabType>('live');
  const [stats, setStats] = useState<LiveCallStats | null>(null);
  const [config, setConfig] = useState<VoiceConfig | null>(null);
  const [agentStats, setAgentStats] = useState<{ availableAgents: number; queuedCalls: number } | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isClearing, setIsClearing] = useState(false);
  const [isTogglingOnline, setIsTogglingOnline] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  // The call opened from the queue: its conversation and summary.
  const [openSessionId, setOpenSessionId] = useState<string | null>(null);
  const [showRoster, setShowRoster] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [showKeypad, setShowKeypad] = useState(false);
  const outboundAvailable = useOutboundAvailable();
  const outgoingCall = useVoiceAgentStore((s) => s.outgoingCall);
  const answeredAt = outgoingCall?.answeredAt;
  const waiting = !!outgoingCall && !answeredAt;
  const participants = useVoiceAgentStore((s) => s.participants);
  const isGuest = useVoiceAgentStore((s) => s.isGuest);
  const { canEnd } = callControls({ activeCall, callContext, participants, isGuest, outgoingCall });
  // Clearing ends every call's session for the organisation: admins only (the BFF enforces it too).
  const canClearSessions = user?.role === 'admin' || user?.role === 'owner';
  const phoneError = useVoiceAgentStore((s) => s.phoneError);
  const micState = useVoiceAgentStore((s) => s.micState);

  const { isConnected, on, off, subscribe, unsubscribe } = useWebSocket();

  const onCall = Boolean(activeCall && callContext);
  const readiness = agentReadiness({ isOnline, onCall, isDeviceReady, isLive: isConnected, phoneError, micState });

  // The call clock is owned here so the console shows it whether or not the
  // dedicated /live-call/active route is open.
  useEffect(() => {
    if (!activeCall || waiting) return;
    // A call this agent placed is timed from when the customer answered.
    const started = answeredAt ? Date.parse(answeredAt) : Date.now() - callDurationSeconds * 1000;
    const t = setInterval(() => setCallDuration(Math.max(0, Math.floor((Date.now() - started) / 1000))), 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCall, waiting, answeredAt]);

  const fetchStats = useCallback(async () => {
    try {
      setError(null);
      const [data, agents] = await Promise.all([
        voiceService.getLiveCallStats(),
        voiceService.getNativeHandoffStats().catch(() => null),
      ]);
      setStats(data);
      if (agents) setAgentStats(agents);
      setLastUpdated(new Date());
    } catch (err: any) {
      setError(err?.message || 'Failed to load statistics');
      console.error('Failed to fetch live call stats:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial fetch and polling.
  useEffect(() => {
    fetchStats();
    voiceService.getConfig().then(setConfig).catch(() => setConfig(null));
    const interval = setInterval(fetchStats, 10000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  // Real-time updates: every voice event re-reads the counters rather than
  // guessing at them, so the cards and the queue never drift apart.
  useEffect(() => {
    subscribe('subscribe_voice_queue', {});
    const refresh = () => fetchStats();

    on('voice:call:started', refresh);
    on('voice:call:ended', refresh);
    on('voice:handoff:requested', refresh);
    on('voice:call:with_agent', refresh);
    on('voice:handoff:updated', refresh);

    return () => {
      unsubscribe('unsubscribe_voice_queue', {});
      off('voice:call:started', refresh);
      off('voice:call:ended', refresh);
      off('voice:handoff:requested', refresh);
      off('voice:call:with_agent', refresh);
      off('voice:handoff:updated', refresh);
    };
  }, [subscribe, unsubscribe, on, off, fetchStats]);

  const handleToggleOnline = useCallback(async () => {
    if (!user?.id) return;
    try {
      setIsTogglingOnline(true);
      setError(null);
      if (isOnline) {
        await voiceService.setAgentOffline(user.id);
        setOnline(false);
      } else {
        // Ask for the microphone now, not when the first call rings: a
        // permission prompt in the middle of a ring loses the call.
        const mic = await requestMicrophone();
        useVoiceAgentStore.getState().setMicState(mic);
        if (mic === 'denied') {
          setError('Your microphone is blocked. Allow it for this site in the browser address bar, then go online again.');
          return;
        }
        // So a call can reach the agent when this tab is in the background.
        try {
          if ('Notification' in window && Notification.permission === 'default') {
            void Notification.requestPermission();
          }
        } catch {
          // Notifications unavailable: the ring still sounds.
        }
        setOnline(true);
        // With the softphone ready, register now; otherwise AgentPhone does it
        // the moment the softphone registers, so no call reaches a browser
        // that cannot ring.
        if (isDeviceReady) await voiceService.setAgentOnline(user.id, user.name);
      }
      await fetchStats();
    } catch (err: any) {
      setError(err?.message || 'Failed to update agent status');
    } finally {
      setIsTogglingOnline(false);
    }
  }, [isOnline, isDeviceReady, user, setOnline, fetchStats]);

  const handleClearSessions = useCallback(async () => {
    setConfirmClear(false);
    try {
      setIsClearing(true);
      setError(null);
      setSuccessMessage(null);
      const result = await voiceService.forceCleanAllSessions();
      setSuccessMessage(`Cleared ${result.cleaned} sessions successfully`);
      await fetchStats();
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setError(err?.message || 'Failed to clear sessions');
      console.error('Failed to clear sessions:', err);
    } finally {
      setIsClearing(false);
    }
  }, [fetchStats]);

  const statCards: { label: string; value: number; icon: ReactNode; accent: Accent }[] = [
    {
      label: 'Live Calls',
      value: stats?.liveCalls ?? 0,
      icon: <PhoneCall className="h-[17px] w-[17px]" />,
      accent: 'brass',
    },
    {
      label: 'Completed Today',
      value: stats?.completedToday ?? 0,
      icon: <Phone className="h-[17px] w-[17px]" />,
      accent: 'green',
    },
    {
      label: 'Completed by AI',
      value: stats?.completedByAI ?? 0,
      icon: <Bot className="h-[17px] w-[17px]" />,
      accent: 'machine',
    },
    {
      label: 'Completed by Human',
      value: stats?.completedByHuman ?? 0,
      icon: <User className="h-[17px] w-[17px]" />,
      accent: 'burgundy',
    },
    {
      label: 'Pending Handoff',
      value: stats?.pendingHandoff ?? 0,
      icon: <Clock className="h-[17px] w-[17px]" />,
      accent: 'terracotta',
    },
  ];

  return (
    <div className="flex h-full min-h-0 flex-col gap-[14px] overflow-hidden bg-[var(--co-canvas)] py-4 pr-4">
      {/* ---------- header card ---------- */}
      <header className={clsx(CARD_CLASS, 'flex-none px-5 py-[18px]')} style={{ ...CARD, borderRadius: 14 }}>
        {/* Row 1: title + subtitle, the utility cluster in the top-right corner. */}
        <div className="flex items-center justify-between gap-[10px]">
          <div className="flex min-w-0 items-center gap-3">
            <div
              className="flex h-[42px] w-[42px] flex-none items-center justify-center rounded-[12px] border"
              style={{
                background: 'var(--co-brass-wash)',
                borderColor: 'var(--co-brass-line)',
                color: 'var(--co-brass-ink)',
              }}
            >
              <Phone className="h-[21px] w-[21px]" />
            </div>
            <div className="min-w-0">
              <h1 className="text-[28px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)]">
                Live Call
              </h1>
              <p className="co-mono mt-[6px] text-[10.5px] font-medium uppercase tracking-[.13em] text-[var(--co-ink-3)]">
                Real-time voice call statistics
              </p>
            </div>
          </div>

          <div className="flex h-[34px] flex-none items-center">
            <PageHeaderMeta />
          </div>
        </div>

        {/* Row 2: the tabs on the left, every other header control on the right. */}
        <div className="mt-3 flex flex-wrap items-center gap-[10px] border-t border-[var(--co-line-subtle)] pt-3">
          <div className="flex items-center gap-[6px] rounded-[10px] bg-[var(--co-selected)] p-1">
            {(
              [
                ['live', 'Live'],
                ['history', 'History'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                aria-pressed={activeTab === key}
                className="co-mono flex items-center gap-[6px] rounded-lg px-3 py-[7px] text-[10.5px] uppercase tracking-[.12em]"
                style={
                  activeTab === key
                    ? {
                        background: 'var(--co-raised)',
                        color: 'var(--co-ink)',
                        fontWeight: 600,
                        boxShadow: 'var(--co-shadow)',
                      }
                    : { color: 'var(--co-ink-3)', fontWeight: 500 }
                }
              >
                {label}
              </button>
            ))}
          </div>

          <div className="flex-1" />

          {/* Connection status */}
          <span
            className="co-mono flex h-[34px] flex-none items-center gap-[6px] rounded-[10px] border px-3 text-[9.5px] font-semibold uppercase tracking-[.12em]"
            style={
              isConnected
                ? {
                    borderColor: 'var(--co-green-line)',
                    background: 'var(--co-green-wash)',
                    color: 'var(--co-green-ink)',
                  }
                : {
                    borderColor: 'var(--co-line)',
                    background: 'var(--co-sunken)',
                    color: 'var(--co-ink-3)',
                  }
            }
          >
            {isConnected ? <Wifi className="h-[13px] w-[13px]" /> : <WifiOff className="h-[13px] w-[13px]" />}
            {isConnected ? 'Live' : 'Connecting…'}
          </span>

          {onCall ? (
            <>
              <button type="button" onClick={isMuted ? unmute : mute} className={HEADER_CONTROL}>
                {isMuted ? (
                  <MicOff className="h-[14px] w-[14px] text-[var(--co-terracotta)]" />
                ) : (
                  <Mic className="h-[14px] w-[14px]" />
                )}
                {isMuted ? 'Unmute' : 'Mute'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/live-call/active')}
                className={HEADER_CONTROL}
              >
                Full context
              </button>
              <button
                type="button"
                onClick={() => hangup()}
                disabled={!canEnd}
                title={canEnd ? undefined : "A ringing call can't be cancelled. It stops within 30 seconds."}
                className={HEADER_PRIMARY}
              >
                End call
              </button>
            </>
          ) : (
            <>
              {outboundAvailable && (
                <button type="button" onClick={() => setShowKeypad(true)} className={HEADER_PRIMARY}>
                  <Grid3x3 className="h-[14px] w-[14px]" />
                  Keypad
                </button>
              )}
              {canClearSessions && (
              <button
                type="button"
                onClick={() => setConfirmClear(true)}
                disabled={isClearing}
                title="Clear all stale sessions from Redis"
                className={clsx(HEADER_CONTROL, 'hover:border-[var(--co-burgundy)]')}
                style={{ borderColor: 'var(--co-burgundy-line)', color: 'var(--co-burgundy)' }}
              >
                <Trash2 className={clsx('h-[14px] w-[14px]', isClearing && 'animate-pulse')} />
                Clear sessions
              </button>
              )}
              <button type="button" onClick={fetchStats} disabled={isLoading} className={HEADER_CONTROL}>
                <RefreshCw className={clsx('h-[14px] w-[14px]', isLoading && 'animate-spin')} />
                Refresh
              </button>
            </>
          )}
        </div>
      </header>

      {/* ---------- body ---------- */}
      <div className="co-scrollbar-none flex min-h-0 flex-1 flex-col gap-[14px] overflow-y-auto">
        {activeTab === 'live' ? (
          <>
            {error && <Banner tone="bad" text={error} />}
            {successMessage && <Banner tone="ok" text={successMessage} />}

            {/* The call you are on. Only while one is connected — the rest of
                the tab is unchanged underneath it. */}
            {onCall && callContext && (
              <CallBand
                phone={callContext.customerPhone}
                seconds={callDurationSeconds}
                isMuted={isMuted}
                recording={Boolean(config?.recordCalls) && !waiting}
                status={
                  waiting && outgoingCall
                    ? outgoingCallLabel(outgoingCall)
                    : outgoingCall
                      ? 'On a call you placed'
                      : 'Connected to you'
                }
                hideClock={waiting}
              />
            )}

            {/* The day's five counters. */}
            <div className="grid flex-none grid-cols-1 gap-[14px] sm:grid-cols-2 lg:grid-cols-5">
              {statCards.map((s) => (
                <FigureCard
                  key={s.label}
                  label={s.label}
                  value={isLoading && !stats ? '—' : s.value}
                  icon={s.icon}
                  accent={s.accent}
                />
              ))}
            </div>

            {/* The floor: your own status and switch, who else is around, what is waiting. */}
            <div className={clsx(CARD_CLASS, 'flex-none overflow-hidden')} style={CARD}>
              <div className="flex flex-wrap items-center justify-between gap-4 px-[18px] py-[14px]">
                <div className="flex min-w-0 flex-wrap items-center gap-x-6 gap-y-2">
                  {/* You: what you can do right now, and why not when you cannot. */}
                  <div className="flex min-w-0 items-center gap-3" aria-live="polite">
                    <span
                      className="co-mono flex flex-none items-center gap-[6px] rounded-full border px-[10px] py-[4px] text-[10px] font-semibold uppercase tracking-[.1em]"
                      style={{
                        background: READINESS_TONE[readiness.tone].wash,
                        borderColor: READINESS_TONE[readiness.tone].line,
                        color: READINESS_TONE[readiness.tone].ink,
                      }}
                    >
                      <span className="h-[7px] w-[7px] rounded-full" style={{ background: READINESS_TONE[readiness.tone].dot }} />
                      {readiness.label}
                    </span>
                    <span className="min-w-0 text-[12px] text-[var(--co-ink-3)]">{readiness.hint}</span>
                  </div>

                  {/* The team: opens who is available, ringing, on a call, last seen. */}
                  <button
                    type="button"
                    onClick={() => setShowRoster((v) => !v)}
                    aria-expanded={showRoster}
                    className="flex items-center gap-2 rounded-lg px-2 py-1 text-[12.5px] text-[var(--co-ink-3)] hover:bg-[var(--co-selected)]"
                  >
                    <Users className="h-[15px] w-[15px]" />
                    <span className="font-semibold text-[var(--co-ink)]">All agents</span>
                    <ChevronDown className={clsx('h-[14px] w-[14px] transition-transform', showRoster && 'rotate-180')} />
                  </button>
                  <span className="flex items-center gap-2 text-[12.5px] text-[var(--co-ink-3)]">
                    <ListOrdered className="h-[15px] w-[15px]" />
                    <span>
                      <span className="co-mono font-semibold tabular-nums text-[var(--co-ink)]">
                        {agentStats?.queuedCalls ?? 0}
                      </span>{' '}
                      calls queued
                    </span>
                  </span>
                </div>

                <button
                  type="button"
                  onClick={handleToggleOnline}
                  disabled={isTogglingOnline || !user?.id}
                  title={isOnline ? 'Go offline: stop being offered calls' : undefined}
                  className={isOnline ? CONTROL : PRIMARY}
                  style={
                    isOnline
                      ? {
                          borderColor: 'var(--co-green-line)',
                          background: 'var(--co-green-wash)',
                          color: 'var(--co-green-ink)',
                        }
                      : undefined
                  }
                >
                  <span
                    className="h-2 w-2 flex-none rounded-full"
                    style={{ background: isOnline ? 'var(--co-green)' : 'var(--co-brass-on)' }}
                  />
                  {isTogglingOnline ? 'Updating…' : isOnline ? 'Online' : 'Go Online'}
                </button>
              </div>

              {showRoster && (
                <div className="border-t border-[var(--co-line)]">
                  <AgentRoster currentUserId={user?.id} onOpenCall={setOpenSessionId} />
                </div>
              )}
            </div>

            {/* The calls themselves — every live session, its status, how long
                it has been waiting and the last thing said. Kept current by the
                same voice events the counters above use. */}
            <VoiceQueue onSessionSelect={(s) => setOpenSessionId(s.sessionId)} />

            {lastUpdated && (
              <p className="co-mono flex-none pb-1 text-center text-[9.5px] uppercase tracking-[.12em] text-[var(--co-ink-3)]">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </p>
            )}
          </>
        ) : (
          <VoiceCallHistory onCallSelect={(call) => call.sessionId && setOpenSessionId(call.sessionId)} />
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmClear}
        onClose={() => setConfirmClear(false)}
        onConfirm={handleClearSessions}
        loading={isClearing}
        variant="danger"
        title="Clear all voice sessions?"
        message={
          'This removes every call from the live view, including calls still in progress and callers waiting ' +
          'for an agent. Use it only to clear calls that are stuck. Tickets and call history are not affected.'
        }
      />

      {showKeypad && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <button
            type="button"
            aria-label="Close keypad"
            className="flex-1 cursor-default bg-black/20"
            onClick={() => setShowKeypad(false)}
          />
          <div className="h-full w-full sm:w-[380px]" style={{ boxShadow: 'var(--co-shadow-card)' }}>
            <DialPad onClose={() => setShowKeypad(false)} />
          </div>
        </div>
      )}

      {/* A call opened from the queue: over the page, on the right. */}
      {openSessionId && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <button
            type="button"
            aria-label="Close call details"
            className="flex-1 cursor-default bg-black/20"
            onClick={() => setOpenSessionId(null)}
          />
          <div className="h-full w-full sm:w-[440px]" style={{ boxShadow: 'var(--co-shadow-card)' }}>
            <VoiceSessionDetail sessionId={openSessionId} onClose={() => setOpenSessionId(null)} />
          </div>
        </div>
      )}
    </div>
  );
}

function Banner({ tone, text }: { tone: 'ok' | 'bad'; text: string }) {
  const ok = tone === 'ok';
  return (
    <div
      className="flex-none border px-[18px] py-[13px] text-[12.5px]"
      style={{
        ...CARD,
        borderColor: ok ? 'var(--co-green-line)' : 'var(--co-burgundy-line)',
        background: ok ? 'var(--co-green-wash)' : 'var(--co-burgundy-wash)',
        color: ok ? 'var(--co-green-ink)' : 'var(--co-burgundy)',
      }}
    >
      {text}
    </div>
  );
}

/** One counter. Same shape as the metric cards elsewhere in the product. */
function FigureCard({
  label,
  value,
  icon,
  accent,
}: {
  label: string;
  value: string | number;
  icon: ReactNode;
  accent: Accent;
}) {
  const tone = ACCENT[accent];
  return (
    <div className={clsx(CARD_CLASS, 'min-w-0 px-[18px] py-4')} style={CARD}>
      {/* The figure sits on the card's bottom edge, so a label that wraps to
          two lines in one card does not push its figure out of line with the
          others in the row. */}
      <div className="flex h-full items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col justify-between self-stretch">
          <p className="co-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
            {label}
          </p>
          <p
            className="co-mono mt-[11px] text-[26px] font-semibold leading-none tabular-nums"
            style={{ color: tone.ink }}
          >
            {value}
          </p>
        </div>
        <div
          className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-lg border"
          style={{ background: tone.wash, borderColor: tone.line, color: tone.ink }}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

/** The one ink band on the page: the call itself. */
function CallBand({
  phone,
  seconds,
  isMuted,
  recording,
  status,
  hideClock,
}: {
  phone: string;
  seconds: number;
  isMuted: boolean;
  recording: boolean;
  status: string;
  hideClock: boolean;
}) {
  return (
    <div
      className="flex flex-none items-center gap-5 px-[22px] py-5"
      style={{ borderRadius: 10, background: 'var(--co-ink)', boxShadow: 'var(--co-shadow-card)' }}
    >
      <div
        className="flex h-[52px] w-[52px] flex-none items-center justify-center rounded-[12px]"
        style={{ background: 'var(--co-brass)', color: 'var(--co-ink)' }}
      >
        <Phone className="h-6 w-6" />
      </div>

      <div className="min-w-0 flex-1">
        <div
          className="co-mono truncate text-[22px] font-bold tracking-[-.018em]"
          style={{ color: 'var(--co-on-ink)' }}
        >
          {voiceService.formatPhoneNumber(phone || '') || 'Unknown number'}
        </div>
        <div
          className="co-mono mt-[6px] text-[10.5px] uppercase tracking-[.12em]"
          style={{ color: 'var(--co-brass)' }}
        >
          {status}
          {isMuted ? ' · you are muted' : ''}
        </div>
      </div>

      <div className="flex-none text-right">
        <div className="co-mono text-[30px] font-semibold tabular-nums" style={{ color: 'var(--co-on-ink)' }}>
          {hideClock ? '' : callClock(seconds)}
        </div>
        {recording && (
          <div className="mt-[7px] flex items-center justify-end gap-[7px]">
            <span className="h-[7px] w-[7px] rounded-full" style={{ background: 'var(--co-green-soft)' }} />
            <span
              className="co-mono text-[9.5px] font-semibold uppercase tracking-[.14em]"
              style={{ color: 'var(--co-brass)' }}
            >
              Recording
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
